import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  KeyIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  TrashIcon,
  XMarkIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ShareIcon,
  UsersIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

interface PasswordEntry {
  id: string;
  name: string;
  username: string | null;
  email: string | null;
  url: string | null;
  category: string | null;
  password_strength: string | null;
  is_shared?: boolean;
  source_family_name?: string | null;
}

interface LinkedFamily {
  id: string;
  family_id: string;
  family_name: string;
}

interface CreatePasswordForm {
  name: string;
  username: string;
  email: string;
  password: string;
  url: string;
  category: string;
}

const strengthColors = {
  weak: 'bg-red-500',
  medium: 'bg-amber-500',
  strong: 'bg-emerald-500',
};

// Helper to get favicon URL from a website URL
function getFaviconUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const hostname = new URL(url).hostname;
    // Using Google's favicon service - it's reliable and caches favicons
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
  } catch {
    return null;
  }
}

// Favicon component with fallback - compact version
function SiteFavicon({ url, isShared }: { url: string | null; isShared?: boolean }) {
  const [hasError, setHasError] = useState(false);
  const faviconUrl = getFaviconUrl(url);
  
  if (!faviconUrl || hasError) {
    // Fallback to key icon
    return (
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isShared 
          ? 'bg-gradient-to-br from-accent-500/20 to-primary-500/20' 
          : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
      }`}>
        <KeyIcon className={`w-4 h-4 ${isShared ? 'text-accent-400' : 'text-amber-400'}`} />
      </div>
    );
  }
  
  return (
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 ${
      isShared 
        ? 'bg-gradient-to-br from-accent-500/10 to-primary-500/10 border border-accent-500/20' 
        : 'bg-white/5 border border-white/10'
    }`}>
      <img 
        src={faviconUrl} 
        alt=""
        className="w-5 h-5 object-contain"
        onError={() => setHasError(true)}
      />
    </div>
  );
}

export default function Passwords() {
  const queryClient = useQueryClient();
  const { currentFamily } = useFamilyStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<PasswordEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<string>('');
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [linkedFamilies, setLinkedFamilies] = useState<LinkedFamily[]>([]);
  const [loadingLinkedFamilies, setLoadingLinkedFamilies] = useState(false);
  
  // Mark password notifications as read when visiting this page
  useMarkCategoryRead('passwords');

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CreatePasswordForm>();
  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, setValue: setEditValue, formState: { errors: editErrors } } = useForm<CreatePasswordForm>();

  const { data: passwords, isLoading } = useQuery({
    queryKey: ['passwords', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/passwords/family/${currentFamily.id}`);
      return response.data as PasswordEntry[];
    },
    enabled: !!currentFamily?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreatePasswordForm) => {
      if (!currentFamily?.id) throw new Error('No family selected');
      const response = await api.post(`/passwords/family/${currentFamily.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwords'] });
      toast.success('Password saved!');
      setShowAddModal(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to save password');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/passwords/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwords'] });
      toast.success('Password deleted');
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreatePasswordForm }) => {
      const response = await api.patch(`/passwords/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwords'] });
      toast.success('Password updated!');
      setShowEditModal(null);
      resetEdit();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    },
  });

  const handleReveal = async (id: string) => {
    if (revealedId === id) {
      setRevealedId(null);
      setRevealedPassword('');
      return;
    }

    try {
      const response = await api.post(`/passwords/${id}/reveal`);
      setRevealedId(id);
      setRevealedPassword(response.data.password);
      setTimeout(() => {
        setRevealedId(null);
        setRevealedPassword('');
      }, 30000); // Auto-hide after 30 seconds
    } catch (error) {
      toast.error('Failed to reveal password');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const generatePassword = async (isEdit = false) => {
    try {
      const response = await api.post('/passwords/generate', {
        length: 20,
        include_uppercase: true,
        include_lowercase: true,
        include_numbers: true,
        include_symbols: true,
      });
      if (isEdit) {
        setEditValue('password', response.data.password);
      } else {
        setValue('password', response.data.password);
      }
      toast.success('Password generated!');
    } catch (error) {
      toast.error('Failed to generate password');
    }
  };

  const handleOpenEdit = async (entry: PasswordEntry) => {
    // Fetch full password to populate edit form
    try {
      const response = await api.post(`/passwords/${entry.id}/reveal`);
      setShowEditModal(entry);
      resetEdit();
      setEditValue('name', entry.name);
      setEditValue('username', entry.username || '');
      setEditValue('email', entry.email || '');
      setEditValue('password', response.data.password);
      setEditValue('url', entry.url || '');
      setEditValue('category', entry.category || '');
    } catch (error) {
      toast.error('Failed to load password for editing');
    }
  };

  const filteredPasswords = passwords?.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const loadLinkedFamilies = async () => {
    if (!currentFamily?.id) return;
    setLoadingLinkedFamilies(true);
    try {
      const response = await api.get(`/family-links/${currentFamily.id}/linked`);
      setLinkedFamilies(response.data || []);
    } catch (error) {
      console.error('Failed to load linked families:', error);
    } finally {
      setLoadingLinkedFamilies(false);
    }
  };
  
  const handleOpenShareModal = async (passwordId: string) => {
    await loadLinkedFamilies();
    setShowShareModal(passwordId);
  };
  
  const handleSharePassword = async (targetFamilyId: string) => {
    if (!currentFamily?.id || !showShareModal) return;
    try {
      await api.post(`/family-links/${currentFamily.id}/share`, {
        resource_type: 'password',
        resource_id: showShareModal,
        target_family_id: targetFamilyId,
      });
      toast.success('Password shared successfully!');
      setShowShareModal(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to share password');
    }
  };

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <KeyIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Family Selected</h2>
          <p className="text-surface-400 mb-6">Select or create a family to manage passwords</p>
          <Link to="/families" className="btn-primary inline-flex">Go to Families</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Password Vault</h1>
          <p className="text-surface-400">{currentFamily.name}</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Add Password</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        <input
          type="text"
          placeholder="Search passwords..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-12"
        />
      </div>

      {/* Passwords */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : filteredPasswords?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <KeyIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Passwords</h2>
          <p className="text-surface-400 mb-6">Add your first password entry</p>
          <button onClick={() => setShowAddModal(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            <span>Add Password</span>
          </button>
        </motion.div>
      ) : (
        <div className="space-y-1.5">
          {filteredPasswords?.map((entry, index) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.015 }}
              className={`card !p-2 ${entry.is_shared ? 'border-l-2 border-l-accent-500' : ''}`}
            >
              <div className="flex items-center gap-2">
                <SiteFavicon url={entry.url} isShared={entry.is_shared} />
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-medium text-white truncate">{entry.name}</h3>
                    {entry.password_strength && (
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${strengthColors[entry.password_strength as keyof typeof strengthColors] || 'bg-surface-500'}`} />
                    )}
                    {entry.is_shared && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-accent-500/20 text-accent-300 flex-shrink-0">
                        {entry.source_family_name || 'Shared'}
                      </span>
                    )}
                  </div>
                </div>
                {/* Action buttons */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => handleReveal(entry.id)}
                    className="p-1.5 rounded text-surface-400 hover:text-white hover:bg-surface-700 transition-colors"
                    title={revealedId === entry.id ? 'Hide' : 'Reveal'}
                  >
                    {revealedId === entry.id ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Revealed section with username + password */}
              {revealedId === entry.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-2 pt-2 border-t border-white/10 space-y-1.5"
                >
                  {/* Username/Email */}
                  {(entry.username || entry.email) && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-surface-500 w-16 flex-shrink-0">User:</span>
                      <code className="flex-1 px-2 py-1 rounded bg-surface-800 text-xs text-surface-300 truncate">
                        {entry.username || entry.email}
                      </code>
                      <button
                        onClick={() => handleCopy(entry.username || entry.email || '')}
                        className="p-1 rounded text-surface-500 hover:text-white hover:bg-surface-700 transition-colors"
                        title="Copy username"
                      >
                        <ClipboardIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {/* Password */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-surface-500 w-16 flex-shrink-0">Pass:</span>
                    <code className="flex-1 px-2 py-1 rounded bg-surface-800 font-mono text-xs text-white truncate">
                      {revealedPassword}
                    </code>
                    <button
                      onClick={() => handleCopy(revealedPassword)}
                      className="p-1 rounded text-surface-500 hover:text-white hover:bg-surface-700 transition-colors"
                      title="Copy password"
                    >
                      <ClipboardIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* URL if present */}
                  {entry.url && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-surface-500 w-16 flex-shrink-0">URL:</span>
                      <a
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-2 py-1 rounded bg-surface-800 text-xs text-primary-400 hover:text-primary-300 truncate"
                      >
                        {entry.url}
                      </a>
                    </div>
                  )}

                  {/* Action buttons - only show for non-shared passwords */}
                  {!entry.is_shared && (
                    <div className="flex items-center gap-1.5 pt-2 border-t border-white/10">
                      <button
                        onClick={() => handleOpenEdit(entry)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-white transition-colors"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleOpenShareModal(entry.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-accent-400 transition-colors"
                      >
                        <ShareIcon className="w-3.5 h-3.5" />
                        Share
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this password?')) {
                            deleteMutation.mutate(entry.id);
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-red-400 transition-colors"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAddModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Password</h2>
                <button onClick={() => setShowAddModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Netflix, Bank Account, etc."
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <p className="text-sm text-red-400">{errors.name.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">Username</label>
                  <input type="text" className="input" placeholder="username" {...register('username')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Email</label>
                  <input type="email" className="input" placeholder="email@example.com" {...register('email')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="••••••••"
                      {...register('password', { required: 'Password is required' })}
                    />
                    <button type="button" onClick={() => generatePassword(false)} className="btn-secondary">
                      <ArrowPathIcon className="w-5 h-5" />
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-red-400">{errors.password.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">URL</label>
                  <input type="url" className="input" placeholder="https://..." {...register('url')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Category</label>
                  <select className="input" {...register('category')}>
                    <option value="">Select category</option>
                    <option value="social">Social Media</option>
                    <option value="finance">Finance</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={createMutation.isPending} className="btn-primary flex-1">
                    {createMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowShareModal(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Share Password</h2>
                <button onClick={() => setShowShareModal(null)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {loadingLinkedFamilies ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : linkedFamilies.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="w-12 h-12 mx-auto mb-3 text-surface-500" />
                  <p className="text-surface-400 mb-2">No linked families</p>
                  <p className="text-surface-500 text-sm">
                    Link with another family first to share passwords
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-surface-300 text-sm mb-4">
                    Select a linked family to share this password with:
                  </p>
                  {linkedFamilies.map((family) => (
                    <button
                      key={family.id}
                      onClick={() => handleSharePassword(family.family_id)}
                      className="w-full p-4 rounded-xl bg-surface-800 border border-surface-700 hover:border-primary-500 transition-colors flex items-center gap-3 text-left"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center">
                        <UsersIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">{family.family_name}</h3>
                        <p className="text-sm text-surface-400">Click to share</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowEditModal(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Edit Password</h2>
                <button onClick={() => setShowEditModal(null)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitEdit((data) => editMutation.mutate({ id: showEditModal.id, data }))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Netflix, Bank Account, etc."
                    {...registerEdit('name', { required: 'Name is required' })}
                  />
                  {editErrors.name && <p className="text-sm text-red-400">{editErrors.name.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">Username</label>
                  <input type="text" className="input" placeholder="username" {...registerEdit('username')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Email</label>
                  <input type="email" className="input" placeholder="email@example.com" {...registerEdit('email')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="••••••••"
                      {...registerEdit('password', { required: 'Password is required' })}
                    />
                    <button type="button" onClick={() => generatePassword(true)} className="btn-secondary">
                      <ArrowPathIcon className="w-5 h-5" />
                    </button>
                  </div>
                  {editErrors.password && <p className="text-sm text-red-400">{editErrors.password.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">URL</label>
                  <input type="url" className="input" placeholder="https://..." {...registerEdit('url')} />
                </div>

                <div className="input-group">
                  <label className="input-label">Category</label>
                  <select className="input" {...registerEdit('category')}>
                    <option value="">Select category</option>
                    <option value="social">Social Media</option>
                    <option value="finance">Finance</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowEditModal(null)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={editMutation.isPending} className="btn-primary flex-1">
                    {editMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

